import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flower2, GraduationCap, Heart, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const ICONS = {
  lotus: Flower2,
  graduation: GraduationCap,
  heart: Heart,
  ashoka: Sparkles,
};

/**
 * Causes preview — 4 themed funds with their YTD raised totals. Distinct
 * from the membership tiers; this is where one-off donations land.
 */
export default function CausesPreview() {
  const [causes, setCauses] = useState([]);
  useEffect(() => {
    api.get("/causes").then((r) => setCauses(r.data.causes || [])).catch(() => {});
  }, []);
  if (!causes.length) return null;

  return (
    <section className="py-24 bg-white" data-testid="home-causes">
      <div className="section-container">
        <div className="max-w-3xl">
          <p className="eyebrow">Where your support goes</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy">
            Four causes. One Sangha. Everyone welcome.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Choose a cause that speaks to you — or split between them. Every donation is tagged
            so you can see exactly where the money goes in our annual report.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {causes.map((c, i) => {
            const Icon = ICONS[c.icon] || Flower2;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className="group relative bg-pearl/40 border-l-4 rounded-sm p-7 hover:-translate-y-0.5 transition-all overflow-hidden"
                style={{ borderLeftColor: c.accent }}
                data-testid={`cause-card-${c.id}`}
              >
                <div className="absolute -right-12 -top-12 opacity-[0.07] pointer-events-none">
                  <Icon size={220} strokeWidth={1} style={{ color: c.accent }} />
                </div>
                <div className="relative">
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex items-center justify-center w-11 h-11 rounded-sm"
                      style={{ backgroundColor: `${c.accent}15` }}
                    >
                      <Icon size={20} style={{ color: c.accent }} strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest" style={{ color: c.accent }}>
                        {c.tagline}
                      </p>
                      <h3 className="font-display text-2xl text-navy">{c.name}</h3>
                    </div>
                  </div>
                  <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">{c.description}</p>
                  <p className="mt-3 text-xs text-slate-500 italic">{c.impact}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      Raised this year:{" "}
                      <span className="font-semibold text-navy">
                        £{Number(c.raised_gbp_ytd || 0).toLocaleString("en-GB")}
                      </span>
                    </div>
                    <Link
                      to={`/donate?cause=${c.id}`}
                      data-testid={`cause-donate-${c.id}`}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs font-medium text-pearl hover:opacity-90"
                      style={{ backgroundColor: c.accent }}
                    >
                      Support this <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div
          className="mt-12 border rounded-sm p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-5"
          style={{ background: "rgba(138,45,59,0.05)", borderColor: "rgba(138,45,59,0.25)" }}
        >
          <Heart size={28} style={{ color: "#8A2D3B" }} className="shrink-0" />
          <div className="flex-1">
            <h3 className="font-display text-2xl text-navy">In urgent need? The Karuṇā Fund is for you.</h3>
            <p className="mt-2 text-slate-700 leading-relaxed">
              Confidential, no-questions-asked hardship grants for community members facing
              bereavement, illness, sudden loss of income or immigration crisis. A trustee will
              reply within 7 working days.
            </p>
          </div>
          <Link
            to="/karuna-fund"
            data-testid="karuna-fund-cta"
            className="px-5 py-2.5 text-pearl rounded-sm text-sm font-medium whitespace-nowrap hover:opacity-90"
            style={{ backgroundColor: "#8A2D3B" }}
          >
            Apply confidentially
          </Link>
        </div>
      </div>
    </section>
  );
}
